import { useEffect, useState } from 'react';
import type {
  IUpcomingFeature,
  IUpcomingFeatureUpdate,
  UpcomingPhase,
} from '@skystone/shared';
import { formatPhaseLabel } from '@skystone/shared';
import { Button, Drawer, DrawerFooter, ErrorBanner } from '@/components/ui';
import { getErrorMessage } from '@/lib/utils';

const PHASE_OPTIONS: UpcomingPhase[] = ['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'];

const TITLE_MAX = 80;
const DESCRIPTION_MAX = 300;

interface UpcomingEditorFormState {
  title: string;
  description: string;
  phase: UpcomingPhase;
  displayOrder: number;
}

interface UpcomingEditorProps {
  open: boolean;
  /** When set, the form opens in edit mode. Null = create mode. */
  initialItem: IUpcomingFeature | null;
  /** Default displayOrder shown in create mode. Server applies max+10 if the user clears this; this is just a friendly seed value. */
  defaultDisplayOrder: number;
  onClose: () => void;
  /** Receives a fully-typed update payload (displayOrder always present). */
  onSave: (data: IUpcomingFeatureUpdate) => Promise<void>;
}

function emptyForm(defaultOrder: number): UpcomingEditorFormState {
  return {
    title: '',
    description: '',
    phase: 'PHASE_2',
    displayOrder: defaultOrder,
  };
}

function fromItem(item: IUpcomingFeature): UpcomingEditorFormState {
  return {
    title: item.title,
    description: item.description,
    phase: item.phase,
    displayOrder: item.displayOrder,
  };
}

export function UpcomingEditor({
  open,
  initialItem,
  defaultDisplayOrder,
  onClose,
  onSave,
}: Readonly<UpcomingEditorProps>) {
  const [form, setForm] = useState<UpcomingEditorFormState>(() =>
    initialItem ? fromItem(initialItem) : emptyForm(defaultDisplayOrder),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialItem ? fromItem(initialItem) : emptyForm(defaultDisplayOrder));
    setError(null);
    setSaving(false);
  }, [open, initialItem, defaultDisplayOrder]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave({
        title: form.title.trim(),
        description: form.description.trim(),
        phase: form.phase,
        displayOrder: form.displayOrder,
      });
    } catch (err) {
      setError(getErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={initialItem ? 'Edit upcoming feature' : 'Create upcoming feature'}
    >
      <form onSubmit={handleSubmit} className="flex h-full flex-col gap-4">
        {error && <ErrorBanner message={error} />}

        <div className="flex flex-col gap-1">
          <label htmlFor="upcoming-title" className="text-sm font-medium">
            Title <span className="text-error">*</span>
          </label>
          <input
            id="upcoming-title"
            type="text"
            required
            maxLength={TITLE_MAX}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            {form.title.length}/{TITLE_MAX}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="upcoming-description" className="text-sm font-medium">
            Description <span className="text-error">*</span>
          </label>
          <textarea
            id="upcoming-description"
            required
            maxLength={DESCRIPTION_MAX}
            rows={4}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            {form.description.length}/{DESCRIPTION_MAX}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="upcoming-phase" className="text-sm font-medium">
            Phase <span className="text-error">*</span>
          </label>
          <select
            id="upcoming-phase"
            value={form.phase}
            onChange={(e) => setForm({ ...form, phase: e.target.value as UpcomingPhase })}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {PHASE_OPTIONS.map((phase) => (
              <option key={phase} value={phase}>
                {formatPhaseLabel(phase)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="upcoming-order" className="text-sm font-medium">
            Display order <span className="text-error">*</span>
          </label>
          <input
            id="upcoming-order"
            type="number"
            required
            min={0}
            step={10}
            value={form.displayOrder}
            onChange={(e) =>
              setForm({ ...form, displayOrder: Number.parseInt(e.target.value, 10) || 0 })
            }
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">
            Lower numbers render first. Existing rows use steps of 10 (10, 20, 30…).
          </p>
        </div>

        <DrawerFooter className="mt-auto">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saving}>
            {initialItem ? 'Save changes' : 'Create'}
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}
